import React, { useEffect } from "react";
import {NumericFormat} from 'react-number-format';
import "./SimpleExchange.css";
import CurrencyExchangerService from "../../../services/CurrencyExchangerService";
import CountrySelect from "../CountrySelect/CountrySelect";
import CurrencySelect from "../CurrencySelect/CurrencySelect";
import { useNavigate } from "react-router-dom";
import { useExchange } from "../../../Context/Exchange";
import {getCurrencyConfig, getDecimalScale} from "../../utils/getDecimalScale.js";

const currencies = ["USDT", "BTC", "ETH", "USD", "EUR", "ILS", "MDL", "CZK", "GBP"];

const SimpleExchange = () => {
    const {
        fromValue, setFromValue,
        fromCurrency, setFromCurrency,
        toValue, setToValue,
        toCurrency, setToCurrency,
        exchangeRate, setExchangeRate,
        selectedCountry, setSelectedCountry,
        reserve,
        minAmount
    } = useExchange();

    const navigate = useNavigate();

    useEffect(() => {
        fetchExchangeRate();
    }, [fromCurrency, toCurrency]);

    const fetchExchangeRate = async () => {
        try {
            const data = await CurrencyExchangerService.fetchCurrencyData(fromCurrency.toLowerCase());

            if (data && data[toCurrency.toLowerCase()]) {
                const rate = data[toCurrency.toLowerCase()];
                setExchangeRate(rate);

                const numericFromValue = parseFloat(fromValue) || 0;
                const newToValue = (numericFromValue * rate);
                setToValue(newToValue);
            } else {
                setExchangeRate(1);
                const numericFromValue = parseFloat(fromValue) || 0;
                setToValue(numericFromValue.toString());
            }
        } catch (error) {
            console.error("Error fetching exchange rate", error);
            setExchangeRate(1);
            const numericFromValue = parseFloat(fromValue) || 0;
            setToValue(numericFromValue.toString());
        }
    };

    const handleExchangeClick = () => {
        navigate('/fast-exchange');
    };

    const handleFromValueChange = (values) => {
        setFromValue(values.value);

        if (exchangeRate) {
            const numericValue = parseFloat(values.value) || 0;
            const convertedValue = (numericValue * exchangeRate);
            setToValue(convertedValue);
        }
    };

    const handleSwap = () => {
        setFromCurrency(toCurrency);
        setToCurrency(fromCurrency);
        setFromValue(toValue);
        setToValue(fromValue);
    };

    const isButtonDisabled = () => {

        const numericFromValue = parseFloat(fromValue) || 0;

        return !fromValue || !toValue || !fromCurrency || !toCurrency || !selectedCountry || numericFromValue <= minAmount;
    };

    return (
        <div className="simple-exchange-container">
            <h3>Быстрый обмен</h3>
            <form className="simple-exchange-form">
                <div className="input-block">
                    <div className="input-from">
                        <label>Отдаете</label>
                        <NumericFormat
                            value={fromValue}
                            onValueChange={handleFromValueChange}
                            thousandSeparator={true}
                            decimalScale={getDecimalScale(fromCurrency)}
                            valueIsNumericString={true}
                            allowNegative={false}
                            isAllowed={(values) => {
                                const { value } = values;
                                if (value.startsWith('0') && value.length > 1 && !value.startsWith('0.')) {
                                    return false;
                                }
                                return true;
                            }}
                            className="number-format-input"
                        />
                    </div>
                    <CurrencySelect
                        selectedCurrency={fromCurrency}
                        onCurrencyChange={setFromCurrency}
                        currencies={currencies}
                    />
                </div>

                <p className="min-amount">
                    Минимальная сумма перевода
                    <NumericFormat
                        value={getCurrencyConfig(fromCurrency).minAmountFormat(minAmount)}
                        thousandSeparator={true}
                        decimalScale={getCurrencyConfig(fromCurrency).decimalScale}
                        displayType="text"
                    /> {fromCurrency}
                </p>

                <div className="swap-icon-block">
                    <svg
                        onClick={handleSwap}
                        className="swap-icon"
                        width="29"
                        height="29"
                        viewBox="0 0 29 29"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="14.5" cy="14.5" r="14.5" transform="rotate(180 14.5 14.5)" fill="#1F2328" />
                        <path d="M16.2346 14.5225C15.9271 14.8815 15.964 15.426 16.3171 15.7387L20.0403 20.3866L20.0481 20.3935C20.3571 20.667 20.806 20.6725 21.1193 20.4291C21.1365 20.4157 21.1534 20.4015 21.1698 20.3866L24.893 15.7387C25.2461 15.426 25.283 14.8815 24.9755 14.5225C24.6679 14.1635 24.1323 14.126 23.7791 14.4386L21.4529 17.8497L21.4529 10.0201C21.4529 9.54402 21.0733 9.1581 20.6049 9.1581C20.1365 9.1581 19.757 9.54402 19.757 10.0201L19.757 17.8494L17.4311 14.4386C17.0778 14.126 16.5422 14.1635 16.2346 14.5225Z" fill="#9F9F9F" />
                        <path d="M4.78738 15.2411C4.47983 14.8822 4.51676 14.3376 4.86986 14.025L8.59306 9.37711L8.60084 9.37017C8.90988 9.09663 9.3587 9.09115 9.67204 9.33456C9.68927 9.34799 9.70616 9.36217 9.72255 9.37711L13.4458 14.025C13.7988 14.3376 13.8358 14.8822 13.5282 15.2411C13.2207 15.6001 12.6851 15.6377 12.3318 15.325L10.0056 11.914L10.0056 19.7436C10.0056 20.2196 9.626 20.6056 9.15764 20.6056C8.68928 20.6056 8.30969 20.2196 8.30969 19.7436L8.30969 11.9143L5.98379 15.325C5.63053 15.6377 5.09493 15.6001 4.78738 15.2411Z" fill="#9F9F9F" />
                    </svg>
                </div>

                <div className="input-block">
                    <div className="input-to">
                        <label>Получаете</label>
                        <NumericFormat
                            readOnly={true}
                            value={toValue}
                            thousandSeparator={true}
                            decimalScale={getDecimalScale(toCurrency)}
                        />
                    </div>
                    <CurrencySelect
                        selectedCurrency={toCurrency}
                        onCurrencyChange={setToCurrency}
                        currencies={currencies}
                    />
                </div>

                <p className="reserve-amount">
                    Наш резерв: {reserve.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {toCurrency}
                </p>

                <div className="rate-info">
                    <span>
                        1 {fromCurrency} = {exchangeRate?.toFixed(6)} {toCurrency}
                        <strong style={{ color: "#eecb12", fontSize: "10px" }}> фиксированный</strong>
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="30px" height="30px" viewBox="0 0 24 24" fill="#9F9F9F"><path d="M14 9v2h-4v-2c0-1.104.897-2 2-2s2 .896 2 2zm10 3c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12zm-8-1h-1v-2c0-1.656-1.343-3-3-3s-3 1.344-3 3v2h-1v6h8v-6z"></path></svg>
                </div>

                <CountrySelect
                    selectedCountry={selectedCountry}
                    setSelectedCountry={setSelectedCountry}
                />

                <button type="button" className="exchange-button" onClick={handleExchangeClick} disabled={isButtonDisabled()}>
                    Обменять
                </button>
            </form>
        </div>
    );
};

export default SimpleExchange;